import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import Kader from '../components/Kader';
import { pcmNaarWav, speelBuffer } from '../lib/audio';
import { opnameOpties } from '../lib/spraak';

/**
 * Meetpagina voor het geluid op iOS, op /meet.
 *
 * WAAROM DIT BESTAAT. Op 11 september 2026 zijn twee oplossingen voor de
 * microfoon op de iPhone allebei gemeten fout. De microfoon per zin opvragen
 * geeft bij elke zin de blauwe toestemmingsbalk van Chrome. De microfoon
 * vasthouden zet iOS in de opnamestand en dan valt het geluid weg (commit
 * 1ecd2a6, teruggedraaid met cc3f772). Wat er tussenin gebeurt was niet na te
 * spelen op een Mac.
 *
 * Deze pagina laat het toestel het ZELF vertellen, in EEN ronde. Per stap
 * staat er wat de AudioContext doet, wat navigator.audioSession zegt, en of
 * er geluid uit kwam. Dat laatste kan alleen een mens horen, dus bij elke
 * toon vraagt de pagina: luid, zacht, of niets. "Zacht" is een eigen antwoord
 * omdat iOS geluid naar het oorspeakertje kan sturen in plaats van het uit te
 * zetten - dat is een ander probleem met een andere oplossing.
 *
 * WAT DRIE RONDES OPLEVERDEN (11 september 2026, iPhone, Chrome, iOS 26.5).
 * Het geluid valt NIET weg maar gaat naar het oortje zodra er een levend
 * microfoonspoor is, en komt terug op de luidspreker zodra de sporen gestopt
 * zijn - ook met nul seconden tussen stoppen en afspelen (toon 1 begon dan
 * ongeveer 300 ms later, maar klonk luid). navigator.audioSession bestaat,
 * maar helpt niet: 'play-and-record' geeft het oortje, en 'playback' weigert
 * de microfoon met "AudioSession category is not compatible with audio
 * capture". De blauwe balk van Chrome komt bij ELKE getUserMedia, ook vlak na
 * een eerdere toestemming.
 *
 * WAT DEZE VERSIE MEET. Stoppen is voorgoed; een spoor UITZETTEN
 * (track.enabled = false) niet. Zet je hem daarna weer aan, dan is er geen
 * nieuwe aanvraag en dus geen balk. De vraag is of iOS bij een uitgezet spoor
 * de luidspreker teruggeeft. Zo ja, dan kan het gesprek de microfoon het hele
 * gesprek vasthouden en hem alleen uitzetten terwijl er iets speelt. Zo nee,
 * dan blijft het bij loslaten voor het afspelen: een balk per beurt.
 *
 * DEZELFDE WEGEN ALS HET GESPREK, MET OPZET. De microfoon wordt aangevraagd
 * met precies dezelfde instellingen als in useSpreken. Toon 1 gaat door de
 * AudioContext op 24 kHz, net als de vertaling. Toon 2 gaat door speelBuffer,
 * de terugvalweg via een <audio>-element met de wekker van vijf seconden.
 * Een meting die een andere weg neemt dan de app, meet iets anders dan de app.
 * MeetPage.test.ts houdt dit vast.
 *
 * ALLEEN NEDERLANDS. Dit is gereedschap voor Giovanni, geen scherm voor een
 * gast. Zestien vertalingen voor een meetpagina zijn zestien vertalingen die
 * niemand leest.
 */

/** WebKit's navigator.audioSession. Staat niet in de standaardtypes. */
interface AudioSessie {
  type: string;
}

function leesAudioSessie(): AudioSessie | null {
  const kandidaat = (navigator as { audioSession?: unknown }).audioSession;
  if (typeof kandidaat !== 'object' || kandidaat === null) return null;
  if (!('type' in kandidaat)) return null;
  return kandidaat as AudioSessie;
}

/** Dezelfde frequentie als waarin de server de spraak levert. */
const SAMPLE_RATE = 24000;
const TOON_SECONDEN = 1.2;
const TOON_1_HZ = 440;
const TOON_2_HZ = 660;
/** Hoe lang we een toon de tijd geven om te beginnen EN af te lopen. */
const TOON_GRENS_MS = 5000;
/** Hoe lang we na het stoppen van de microfoon blijven kijken of iets omslaat. */
const VOLG_MS = 5000;
const VOLG_STAP_MS = 250;

/** Een sinus als 16-bits PCM, met een korte in- en uitloop tegen de tik. */
function maakToon(hz: number): Int16Array {
  const n = Math.round(SAMPLE_RATE * TOON_SECONDEN);
  const uit = new Int16Array(n);
  const flank = Math.round(SAMPLE_RATE * 0.02);
  for (let i = 0; i < n; i++) {
    const omhullende = Math.min(1, i / flank, (n - 1 - i) / flank);
    uit[i] = Math.round(Math.sin((2 * Math.PI * hz * i) / SAMPLE_RATE) * 0.6 * 32767 * omhullende);
  }
  return uit;
}

function wacht(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Wat de pagina na een stap aan Giovanni vraagt. Alleen een mens hoort of het
 * luid of zacht was, en alleen een mens ziet de toestemmingsbalk van Chrome.
 */
type VraagId = 'toon1' | 'toon2' | 'balk';

interface Keuze {
  waarde: string;
  tekst: string;
  kleur: string;
}

const KLEUR_GOED = 'border-emerald-250 bg-emerald-50 text-emerald-650';
const KLEUR_MIDDEN = 'border-gio-blue/30 bg-gio-blue/5 text-gio-blue';
const KLEUR_SLECHT = 'border-gio-danger/30 bg-gio-danger/5 text-gio-danger';

const GEHOORD: Keuze[] = [
  { waarde: 'luid', tekst: 'Luid', kleur: KLEUR_GOED },
  { waarde: 'zacht', tekst: 'Zacht / oortje', kleur: KLEUR_MIDDEN },
  { waarde: 'niets', tekst: 'Niets', kleur: KLEUR_SLECHT },
];
const BALK: Keuze[] = [
  { waarde: 'ja', tekst: 'Ja, balk gezien', kleur: KLEUR_SLECHT },
  { waarde: 'nee', tekst: 'Nee, geen balk', kleur: KLEUR_GOED },
];

const VRAGEN: Record<VraagId, { naam: string; keuzes: Keuze[] }> = {
  toon1: { naam: 'Toon 1 (lage toon): wat hoorde je?', keuzes: GEHOORD },
  toon2: { naam: 'Toon 2 (hoge toon): wat hoorde je?', keuzes: GEHOORD },
  balk: { naam: 'Kwam de blauwe toestemmingsbalk van Chrome?', keuzes: BALK },
};

const TONEN: VraagId[] = ['toon1', 'toon2'];
const BALK_EN_TONEN: VraagId[] = ['balk', ...TONEN];

interface Stap {
  id: string;
  naam: string;
  uitleg: string;
  /** Wat de pagina na deze stap vraagt. Leeg: meteen door. */
  vragen: VraagId[];
  doe: () => Promise<void>;
}

type StapStand = 'wacht' | 'bezig' | 'vraag';

export default function MeetPage() {
  const ctxRef = useRef<AudioContext | null>(null);
  const stroomRef = useRef<MediaStream | null>(null);
  const beginRef = useRef(performance.now());
  const [log, setLog] = useState<string[]>([]);
  const [huidige, setHuidige] = useState(0);
  const [stand, setStand] = useState<StapStand>('wacht');
  const [antwoorden, setAntwoorden] = useState<Partial<Record<VraagId, string>>>({});
  const [kopieStand, setKopieStand] = useState('');

  const noteer = useCallback((tekst: string) => {
    const t = ((performance.now() - beginRef.current) / 1000).toFixed(1);
    setLog((oud) => [...oud, `[${t.padStart(6)} s] ${tekst}`]);
  }, []);

  /** Een regel die in EEN oogopslag laat zien waar alles staat. */
  const samenvatting = useCallback((): string => {
    const ctx = ctxRef.current;
    const sporen = stroomRef.current?.getAudioTracks() ?? [];
    const levend = sporen.filter((s) => s.readyState === 'live').length;
    return (
      `context=${ctx?.state ?? 'geen'} ` +
      `sessie=${leesAudioSessie()?.type ?? 'geen'} ` +
      `micsporen=${levend} levend van ${sporen.length}`
    );
  }, []);

  const maakContext = useCallback((): AudioContext => {
    if (ctxRef.current !== null && ctxRef.current.state !== 'closed') return ctxRef.current;
    const Ctx =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    ctxRef.current = new Ctx({ sampleRate: SAMPLE_RATE });
    return ctxRef.current;
  }, []);

  /** Toon 1: door de AudioContext, zoals de vertaling. */
  const speelToon1 = useCallback(async (): Promise<void> => {
    const ctx = maakContext();
    noteer(`toon 1 (Web Audio, ${TOON_1_HZ} Hz): vooraf ${samenvatting()}`);
    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
        noteer(`toon 1: resume gedaan, context=${ctx.state}`);
      } catch (fout) {
        noteer(`toon 1: resume GEWEIGERD: ${fout instanceof Error ? fout.message : '?'}`);
        return;
      }
    }
    const toon = maakToon(TOON_1_HZ);
    const buffer = ctx.createBuffer(1, toon.length, SAMPLE_RATE);
    const kanaal = buffer.getChannelData(0);
    for (let i = 0; i < toon.length; i++) kanaal[i] = (toon[i] ?? 0) / 32768;

    const start = performance.now();
    await new Promise<void>((klaar) => {
      const bron = ctx.createBufferSource();
      bron.buffer = buffer;
      bron.connect(ctx.destination);
      const wekker = setTimeout(() => {
        noteer(`toon 1: GEEN einde binnen ${TOON_GRENS_MS / 1000} s, context=${ctx.state}`);
        klaar();
      }, TOON_GRENS_MS);
      bron.onended = () => {
        clearTimeout(wekker);
        noteer(`toon 1: afgelopen na ${Math.round(performance.now() - start)} ms`);
        klaar();
      };
      bron.start();
    });
  }, [maakContext, noteer, samenvatting]);

  /** Toon 2: als WAV door een <audio>-element, de terugvalweg van de app. */
  const speelToon2 = useCallback(async (): Promise<void> => {
    noteer(`toon 2 (audio-element, ${TOON_2_HZ} Hz): vooraf ${samenvatting()}`);
    const toon = maakToon(TOON_2_HZ);
    const bytes = new Uint8Array(toon.buffer, toon.byteOffset, toon.byteLength);
    const start = performance.now();
    try {
      await speelBuffer(pcmNaarWav(bytes, SAMPLE_RATE), 'audio/wav');
      noteer(`toon 2: afgelopen na ${Math.round(performance.now() - start)} ms`);
    } catch (fout) {
      noteer(
        `toon 2: FOUT na ${Math.round(performance.now() - start)} ms: ` +
          `${fout instanceof Error ? fout.message : '?'}`,
      );
    }
  }, [noteer, samenvatting]);

  const speelBeide = useCallback(async (): Promise<void> => {
    await speelToon1();
    await wacht(400);
    await speelToon2();
    noteer(`na beide tonen: ${samenvatting()}`);
  }, [speelToon1, speelToon2, noteer, samenvatting]);

  /** De microfoon, PRECIES zoals useSpreken hem vraagt. */
  const micAan = useCallback(async (): Promise<void> => {
    noteer(`microfoon aan: vooraf ${samenvatting()}`);
    try {
      const stroom = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      stroomRef.current = stroom;
      noteer(`microfoon aan: direct erna ${samenvatting()}`);
      await wacht(500);
      noteer(`microfoon aan: na 500 ms ${samenvatting()}`);
    } catch (fout) {
      noteer(
        `microfoon aan: GEWEIGERD: ${fout instanceof Error ? `${fout.name}: ${fout.message}` : '?'}`,
      );
    }
  }, [noteer, samenvatting]);

  /** Sporen stoppen, zonder te wachten. Dit is wat het gesprek zou doen. */
  const stopSporen = useCallback((): void => {
    stroomRef.current?.getTracks().forEach((s) => s.stop());
    noteer(`microfoon uit: direct erna ${samenvatting()}`);
  }, [noteer, samenvatting]);

  /** Sporen stoppen en dan vijf seconden kijken of er iets omslaat. */
  const micUit = useCallback(async (): Promise<void> => {
    stopSporen();
    const begin = performance.now();
    let vorige = samenvatting();
    for (let t = 0; t < VOLG_MS; t += VOLG_STAP_MS) {
      await wacht(VOLG_STAP_MS);
      const nu = samenvatting();
      if (nu !== vorige) {
        noteer(`microfoon uit: na ${Math.round(performance.now() - begin)} ms veranderd: ${nu}`);
        vorige = nu;
      }
    }
    noteer(`microfoon uit: na ${VOLG_MS / 1000} s ${vorige}`);
    stroomRef.current = null;
  }, [stopSporen, noteer, samenvatting]);

  /** Spoor aan- of uitzetten ZONDER het te stoppen. Geen nieuwe aanvraag. */
  const spoorZet = useCallback(
    (aan: boolean): void => {
      const sporen = stroomRef.current?.getAudioTracks() ?? [];
      sporen.forEach((sp) => {
        sp.enabled = aan;
      });
      const stand = sporen.map((sp) => `${sp.readyState}/${sp.enabled ? 'aan' : 'uit'}`).join(',');
      noteer(`spoor ${aan ? 'AAN' : 'UIT'} gezet: ${sporen.length} spoor (${stand}); ${samenvatting()}`);
    },
    [noteer, samenvatting],
  );

  /**
   * Twee seconden opnemen op het vastgehouden spoor, zoals het gesprek dat per
   * zin doet. Meet of er na het weer aanzetten echt geluid binnenkomt.
   */
  const opnameProef = useCallback(async (): Promise<void> => {
    const stroom = stroomRef.current;
    if (stroom === null) {
      noteer('opnameproef: geen stroom, overgeslagen');
      return;
    }
    const stukken: Blob[] = [];
    try {
      // Dezelfde instellingen als het gesprek, ook de bitsnelheid.
      const recorder = new MediaRecorder(stroom, opnameOpties());
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) stukken.push(e.data);
      };
      recorder.start();
      noteer('opnameproef: 2 s opnemen, zeg iets');
      await wacht(2000);
      recorder.stop();
      await wacht(400);
      const bytes = stukken.reduce((n, b) => n + b.size, 0);
      noteer(`opnameproef: ${bytes} bytes (${recorder.mimeType}, ${recorder.audioBitsPerSecond} bit/s); ${samenvatting()}`);
    } catch (fout) {
      noteer(`opnameproef: FOUT: ${fout instanceof Error ? `${fout.name}: ${fout.message}` : '?'}`);
    }
  }, [noteer, samenvatting]);

  const stappen: Stap[] = [
    {
      id: 'speel1',
      naam: 'Alleen afspelen',
      uitleg: 'Twee tonen, zonder microfoon. Dit is het ijkpunt.',
      vragen: TONEN,
      doe: speelBeide,
    },
    {
      id: 'micAan',
      naam: 'Microfoon aan',
      uitleg: 'Vraagt de microfoon zoals het gesprek dat doet, en houdt hem vast.',
      vragen: ['balk'],
      doe: micAan,
    },
    {
      id: 'speel2',
      naam: 'Afspelen met spoor aan',
      uitleg: 'Ter controle. In de vorige rondes was dit zacht.',
      vragen: TONEN,
      doe: speelBeide,
    },
    {
      id: 'spoorUit',
      naam: 'Spoor UIT en meteen afspelen',
      uitleg:
        'Het spoor wordt uitgezet, niet gestopt. Geeft iOS dan de luidspreker terug? ' +
        'Dit is de vraag van deze ronde.',
      vragen: TONEN,
      doe: async (): Promise<void> => {
        spoorZet(false);
        await speelBeide();
      },
    },
    {
      id: 'spoorAan',
      naam: 'Spoor weer AAN, 2 s opnemen, afspelen',
      uitleg:
        'Zeg iets tijdens de opname. Komt er geluid binnen zonder nieuwe balk? ' +
        'En wordt het afspelen daarna weer zacht?',
      vragen: BALK_EN_TONEN,
      doe: async (): Promise<void> => {
        spoorZet(true);
        await opnameProef();
        await speelBeide();
      },
    },
    {
      id: 'opruimen',
      naam: 'Opruimen',
      uitleg: 'Microfoon uit.',
      vragen: [],
      doe: micUit,
    },
  ];

  const klaar = huidige >= stappen.length;
  const stap = stappen[huidige];

  // Het toestel beschrijven bij het openen. De ref houdt StrictMode tegen,
  // die in ontwikkeling elk effect twee keer draait.
  const beschrevenRef = useRef(false);
  useEffect(() => {
    if (beschrevenRef.current) return;
    beschrevenRef.current = true;
    noteer(`meting ${new Date().toISOString()} (meetpagina v3)`);
    noteer(`browser: ${navigator.userAgent}`);
    noteer(`beveiligd: ${window.isSecureContext ? 'ja' : 'nee'}`);
    const s = leesAudioSessie();
    noteer(
      s === null
        ? 'navigator.audioSession: BESTAAT NIET'
        : `navigator.audioSession: bestaat, type='${s.type}'`,
    );
    noteer(`AudioContext bij openen: ${maakContext().state}`);
  }, [noteer, maakContext]);

  async function doeStap(): Promise<void> {
    if (stap === undefined) return;
    setStand('bezig');
    setAntwoorden({});
    noteer(`== stap ${huidige + 1}: ${stap.naam} ==`);
    try {
      await stap.doe();
    } catch (fout) {
      noteer(`stap ${huidige + 1}: FOUT: ${fout instanceof Error ? fout.message : '?'}`);
    }
    if (stap.vragen.length > 0) {
      setStand('vraag');
    } else {
      setHuidige((n) => n + 1);
      setStand('wacht');
    }
  }

  function beantwoord(vraag: VraagId, antwoord: string): void {
    if (stap === undefined) return;
    const nieuw = { ...antwoorden, [vraag]: antwoord };
    setAntwoorden(nieuw);
    noteer(`antwoord ${vraag}: ${antwoord.toUpperCase()}`);
    if (stap.vragen.every((v) => nieuw[v] !== undefined)) {
      setHuidige((n) => n + 1);
      setStand('wacht');
    }
  }

  async function kopieer(): Promise<void> {
    try {
      await navigator.clipboard.writeText(log.join('\n'));
      setKopieStand('Gekopieerd. Plak het in de chat.');
    } catch {
      setKopieStand('Kopiëren lukte niet. Selecteer de tekst hieronder met de hand.');
    }
  }

  return (
    <Kader
      titel="Geluidsmeting"
      onderschrift={
        'Leg de telefoon op tafel, volume aan, stille schakelaar uit. Tik één keer per knop. ' +
        'Luid = hoorbaar vanaf tafel. Zacht = alleen bij je oor.'
      }
    >
      {!klaar && stap !== undefined && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => void doeStap()}
            disabled={stand !== 'wacht'}
            className="w-full py-3 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                       font-bold text-sm transition-colors duration-155
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {stand === 'bezig' ? 'Bezig… even wachten' : `Stap ${huidige + 1} van ${stappen.length}: ${stap.naam}`}
          </button>
          <p className="mt-2 text-xs text-slate-550">{stap.uitleg}</p>
        </div>
      )}

      {stand === 'vraag' && stap !== undefined && (
        <div className="mb-4 space-y-3 px-3 py-3 bg-gio-blue/8 border-2 border-gio-blue/25 rounded-xl">
          {stap.vragen.map((v) => (
            <Vraag
              key={v}
              naam={VRAGEN[v].naam}
              keuzes={VRAGEN[v].keuzes}
              keuze={antwoorden[v] ?? null}
              kies={(w) => beantwoord(v, w)}
            />
          ))}
        </div>
      )}

      {klaar && (
        <div className="mb-4 px-3 py-3 bg-emerald-50 border-2 border-emerald-250 rounded-xl">
          <p className="text-sm font-bold text-emerald-650">Klaar. Stuur het verslag door.</p>
          <button
            type="button"
            onClick={() => void kopieer()}
            className="mt-2 w-full py-3 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                       font-bold text-sm transition-colors duration-155"
          >
            Kopieer verslag
          </button>
          {kopieStand !== '' && <p className="mt-2 text-xs text-slate-650">{kopieStand}</p>}
        </div>
      )}

      <ol className="space-y-1.5 mb-4">
        {stappen.map((s, i) => {
          const gedaan = i < huidige;
          const nu = i === huidige && !klaar;
          return (
            <li
              key={s.id}
              className={`flex items-center justify-between gap-2 border-2 rounded-xl px-3 py-2 text-xs ${
                gedaan
                  ? 'border-emerald-250 bg-emerald-50 text-emerald-650'
                  : nu
                    ? 'border-gio-blue/30 bg-gio-blue/5 text-gio-blue font-bold'
                    : 'border-slate-250 bg-slate-50 text-slate-450'
              }`}
            >
              <span>
                {i + 1}. {s.naam}
              </span>
              <span className="font-mono">{gedaan ? '✓' : nu ? '…' : '–'}</span>
            </li>
          );
        })}
      </ol>

      <p className="text-[11px] font-bold text-gio-dark mb-1">Verslag</p>
      {/* Zo hoog als het verslag zelf. Een vak van twaalf regels met een eigen
          scrollbalk was op een telefoon niet te fotograferen: negentig regels
          in tien zichtbare. Nu scrolt de hele pagina en zijn een paar
          screenshots genoeg. */}
      <textarea
        readOnly
        value={log.join('\n')}
        rows={Math.max(12, log.length + 1)}
        className="w-full text-[10px] font-mono leading-snug p-2 border-2 border-slate-250 rounded-xl
                   bg-slate-50 text-slate-650"
      />
    </Kader>
  );
}

function Vraag({
  naam,
  keuzes,
  keuze,
  kies,
}: {
  naam: string;
  keuzes: Keuze[];
  keuze: string | null;
  kies: (waarde: string) => void;
}) {
  const knop = (k: Keuze): ReactElement => (
    <button
      key={k.waarde}
      type="button"
      onClick={() => kies(k.waarde)}
      disabled={keuze !== null}
      className={`flex-1 py-2 rounded-lg border-2 text-xs font-bold transition-colors duration-155
                  disabled:cursor-not-allowed ${
                    keuze === k.waarde ? k.kleur : 'border-slate-250 bg-white text-slate-650'
                  } ${keuze !== null && keuze !== k.waarde ? 'opacity-40' : ''}`}
    >
      {k.tekst}
    </button>
  );
  return (
    <div>
      <p className="text-xs font-bold text-gio-dark mb-1.5">{naam}</p>
      <div className="flex gap-2">{keuzes.map(knop)}</div>
    </div>
  );
}
